import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { CUSTOM_VALUE_SET_NAME } from '../../../../../../../common/model/input/value-sets/value-sets';
import {
  Dimension,
  ORDERED_DIMENSIONS,
  ORDERED_IMPACTS_BY_DIMENSION,
  Valence,
} from '../../../../../../../common/model/types';
import { getValueSet, getValueSetName } from '../../../../../../untidy/redux/selectors';
import { getNormalizedValue, getPossibleUniqueValueOfValence, getValueSetMinMax } from './shared';
import {
  StateHooksOfImpactCategories,
  StateHooksOfValences,
  StateHooksOfValueSetSelector,
  ValueSetWithName,
} from './types';

/**
 * OA: A somewhat daunting nested-reducer-exercise to give each impact category its own state and setter for both valences.
 * This may give an increase in performance compared to keeping everything in one object, because now react
 * probably only triggers rerender of one changed radio button at a time instead of everything always.
 */
export function initializeSelectionStates(
  setSelectedValueSet: (vs: ValueSetWithName) => void,
): StateHooksOfValueSetSelector {
  const valueSet = useSelector(getValueSet);
  const name = useSelector(getValueSetName);
  const valueSetWithName = { valueSet, name };
  const { min, max } = getValueSetMinMax(valueSet);
  const allHooks = ORDERED_DIMENSIONS.reduce((statesOfDimension, dimension) => {
    statesOfDimension[dimension] = [Valence.POSITIVE, Valence.NEGATIVE].reduce(
      getValenceReducer(dimension, valueSetWithName, setSelectedValueSet, max, min),
      {} as StateHooksOfValences,
    );
    return statesOfDimension;
  }, {} as StateHooksOfValueSetSelector);

  return allHooks;
}

function getValenceReducer(
  dimension: Dimension,
  valueSetWithName: ValueSetWithName,
  setSelectedValueSet: (vs: ValueSetWithName) => void,
  max: number,
  min: number,
): (
  previousValue: StateHooksOfValences,
  currentValue: Valence,
  currentIndex: number,
  array: Valence[],
) => StateHooksOfValences {
  return (statesOfValence, valence) => {
    const impactCategories = ORDERED_IMPACTS_BY_DIMENSION[dimension];
    const onlyUniqValue = getPossibleUniqueValueOfValence(impactCategories, valence, valueSetWithName.valueSet);
    const [valueOfValence, setValueOfValence] = useState<number | undefined>(
      onlyUniqValue !== undefined ? onlyUniqValue : undefined,
    );

    const setValueOfValenceAndMaybeUpdateValueSet = (
      newValue: number | undefined,
      updateSelectedValueSet?: boolean,
    ) => {
      setValueOfValence(newValue);
      if (!updateSelectedValueSet) {
        return;
      }
      const updatedValueSet = { ...valueSetWithName.valueSet };
      if (newValue !== undefined) {
        impactCategories.forEach(i => {
          updatedValueSet[i][valence] = newValue;
        });
        setSelectedValueSet({ name: CUSTOM_VALUE_SET_NAME, valueSet: { ...updatedValueSet } });
      }
    };

    statesOfValence[valence] = {
      valueStateHookOfValence: [valueOfValence, setValueOfValenceAndMaybeUpdateValueSet],
      openStateHookOfValence: useState<boolean>(onlyUniqValue === undefined),
      stateHooksOfImpactCategories: impactCategories.reduce(
        getImpactCategoryReducer(valueSetWithName, valence, max, min, setSelectedValueSet),
        {} as StateHooksOfImpactCategories,
      ),
    };
    return statesOfValence;
  };
}

function getImpactCategoryReducer(
  valueSetWithName: ValueSetWithName,
  valence: Valence,
  max: number,
  min: number,
  setSelectedValueSet: (vs: ValueSetWithName) => void,
): (
  previousValue: StateHooksOfImpactCategories,
  currentValue: ImpactCategoryId,
  currentIndex: number,
  array: ImpactCategoryId[],
) => StateHooksOfImpactCategories {
  return (statesOfImpactCategory, impactCategoryId) => {
    const valueFromValueSet = valueSetWithName.valueSet[impactCategoryId][valence];
    const normalizedValue = getNormalizedValue(valueFromValueSet, min, max, valueSetWithName.name);
    const [value, setValue] = useState(normalizedValue);

    const setValueOfCategoryAndMaybeUpdateValueSet = (newValue: number, updateSelectedValueSet?: boolean) => {
      setValue(newValue);
      if (!updateSelectedValueSet) {
        return;
      }
      const updatedValueSet = { ...valueSetWithName.valueSet };
      if (newValue !== undefined) {
        updatedValueSet[impactCategoryId][valence] = newValue;
        setSelectedValueSet({ name: CUSTOM_VALUE_SET_NAME, valueSet: { ...updatedValueSet } });
      }
    };

    statesOfImpactCategory[impactCategoryId] = [value, setValueOfCategoryAndMaybeUpdateValueSet];
    return statesOfImpactCategory;
  };
}
